// apps/merchant-v3/src/store/sidebar.ts
//
// Phase 6 Wave 1 Task 02 — Zustand store with persist middleware for
// Sidebar collapse + group open/close state. LocalStorage key
// `merchant-v3.sidebar.state.v1` (versioned per CLAUDE.md persistence
// rule — `merchant-v3.*.v1` prefix so future v2 migrations don't clobber).
//
// vg-binding: PLAN:task-02
// vg-binding: P6.D-04

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type SidebarGroupKey = 'products' | 'stores' | 'finance' | 'settings';

export interface SidebarState {
  /** When true, sidebar renders icon-only at 64px; false = expanded 256px. */
  collapsed: boolean;
  /** Mobile off-canvas drawer open state. NOT persisted (session-only). */
  mobileOpen: boolean;
  /** Per-group open/close state for the 4 collapsible groups. */
  groupsOpen: Record<SidebarGroupKey, boolean>;
  toggleCollapsed: () => void;
  setCollapsed: (v: boolean) => void;
  setMobileOpen: (v: boolean) => void;
  toggleGroup: (key: SidebarGroupKey) => void;
  /**
   * Set one group's open state without disturbing the others.
   *
   * dbg-20260911-080936 — the sidebar opens the group holding the current page
   * on arrival, and it must not close the rest to do it. `toggleGroup` is an
   * accordion by design (a deliberate UX choice for clicks), so navigation
   * needed a non-accordion door of its own rather than a second interpretation
   * of the same action.
   */
  setGroupOpen: (key: SidebarGroupKey, open: boolean) => void;
}

const DEFAULT_GROUPS_OPEN: Record<SidebarGroupKey, boolean> = {
  products: false,
  stores: false,
  finance: false,
  settings: false,
};

const ALL_CLOSED: Record<SidebarGroupKey, boolean> = {
  products: false,
  stores: false,
  finance: false,
  settings: false,
};

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      collapsed: false,
      mobileOpen: false,
      groupsOpen: { ...DEFAULT_GROUPS_OPEN },
      toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
      setCollapsed: (v) => set({ collapsed: v }),
      setMobileOpen: (v) => set({ mobileOpen: v }),
      // Accordion behavior: opening a group collapses all others. Re-clicking
      // an open group closes it (all groups can be closed simultaneously).
      setGroupOpen: (key, open) =>
        set((s) =>
          s.groupsOpen[key] === open
            ? s
            : { groupsOpen: { ...s.groupsOpen, [key]: open } },
        ),
      toggleGroup: (key) =>
        set((s) => {
          const isOpen = s.groupsOpen[key];
          return {
            groupsOpen: isOpen ? { ...ALL_CLOSED } : { ...ALL_CLOSED, [key]: true },
          };
        }),
    }),
    {
      name: 'merchant-v3.sidebar.state.v3',
      storage: createJSONStorage(() => localStorage),
      version: 3,
      partialize: (s) => ({ collapsed: s.collapsed, groupsOpen: s.groupsOpen }),
    },
  ),
);
